import { Controller } from '@hotwired/stimulus';
import {
  batchSetNodeKVPair,
  feedbackCardRatingsIncomplete,
  normalizeActions,
  normalizeNodeUid,
  getDusFromDom,
  mergeDocumentUserStateForContinueSync,
  syncAllContinueButtonsForDocument,
  scheduleScrollOpenedCardTitleIntoView,
  findAnswerCommentInActivePane,
  revealAnswerCommentElement,
} from '../../utils/case_experience/case_experience_in_rails';
import {
  finishCaseCardJustRevealed,
  scheduleStudentCaseCompletionModalsAfterUnlock,
  stopStudentCaseConfetti,
} from '../../utils/case_experience/completion_modals';
import {
  isContinueButtonName,
  isStudentUnlockContinueFlow,
  runStudentContinueUnlock,
  runStudentFeedbackSubmitUnlock,
} from '../../utils/case_experience/continue_unlock';

export default class extends Controller {
  static values = {
    uid: String,
    onClick: Array,
    name: String,
    intent: String,
    documentId: Number,
    userId: Number,
    invisible: Boolean,
    inactive: Number,
    aiSummaryStatementAnswerCommentShown: {
      type: Boolean,
      default: false,
    },
  };

  _isSubmitLike() {
    return this.intentValue === 'unlock_feedback_submit' || this.nameValue?.toLowerCase() === 'submit';
  }

  connect() {
    this.updateButtonState();
  }

  disconnect() {
    stopStudentCaseConfetti();
  }

  invisibleValueChanged() {
    this.updateButtonState();
  }

  inactiveValueChanged() {
    this.updateButtonState();
  }

  updateButtonState() {
    // Visibility
    this.element.style.display = this.invisibleValue ? 'none' : '';

    // Disabled state
    const button = this.element.querySelector('button');
    if (!button) return;

    const name = this.nameValue?.toLowerCase();
    const rawInactive = this.element.getAttribute('data-case-experience--button-action-inactive-value');
    const submitWithoutPersistedInactive =
      name === 'submit' && (rawInactive === null || rawInactive === '');

    const feedbackScope =
      this.element.closest('.feedback-card-content') ||
      this.element.closest('.doc-section.feedback_card');
    const feedbackRatingBars = feedbackScope
      ? [...feedbackScope.querySelectorAll('[data-controller~="case-experience--rating-bar"]')]
      : [];
    const feedbackRatingsIncomplete =
      feedbackScope && feedbackCardRatingsIncomplete(feedbackScope);

    if (submitWithoutPersistedInactive && feedbackRatingBars.length === 0) {
      return;
    }

    let inactiveDisabled = false;
    if (!submitWithoutPersistedInactive) {
      inactiveDisabled = this.inactiveValue === 2;
    }

    const isDisabled = inactiveDisabled || feedbackRatingsIncomplete;
    button.disabled = isDisabled;
    button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
  }

  async handleClick(event) {
    event.preventDefault();

    // Accessibility: Progress bar ARIA update and live region announcement for Continue button
    const buttonTitle = this.nameValue?.toLowerCase();
    if (buttonTitle === 'continue') {
      const progressBar = document.querySelector('.progress-container');
      if (progressBar) {
        // Get latest progress value
        let progress = parseInt(progressBar.getAttribute('aria-valuenow'), 10);
        progressBar.setAttribute('aria-valuenow', String(progress));
        let label;
        if (progress === 0 || isNaN(progress)) {
          label = 'Not Started';
        } else if (progress === 100) {
          label = '100% of the case is completed';
        } else {
          label = `${Math.round(progress)}% of the case is completed`;
        }
        progressBar.setAttribute('aria-label', label);
      }
      // Use a dedicated live region for announcement
      let alertRegion = document.getElementById('screenreader-alert');
      if (!alertRegion) {
        alertRegion = document.createElement('div');
        alertRegion.id = 'screenreader-alert';
        alertRegion.setAttribute('aria-live', 'assertive');
        alertRegion.style.position = 'absolute';
        alertRegion.style.left = '-9999px';
        alertRegion.style.height = '1px';
        alertRegion.style.width = '1px';
        alertRegion.style.overflow = 'hidden';
        document.body.appendChild(alertRegion);
      }
      if (progressBar && alertRegion) {
        // Clear live region first to force announcement
        alertRegion.textContent = '';
        setTimeout(() => {
          const announceText = progressBar.getAttribute('aria-label') || 'Progress bar focused';
          alertRegion.textContent = announceText;
        }, 1500);
      }
    }

    if (isContinueButtonName(buttonTitle) && isStudentUnlockContinueFlow()) {
      const unlockPayload = await runStudentContinueUnlock({
        element: this.element,
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        application: this.application,
      });
      if (finishCaseCardJustRevealed(unlockPayload)) {
        this.handleCompletionModals(unlockPayload);
      }
      return;
    }

    if (!this.hasOnClickValue || !this.onClickValue) {
      return;
    }

    const normalizedActions = normalizeActions(this.onClickValue);

    // Run non-setNodeKVPair actions first
    normalizedActions.forEach((action) => {
      if (action.action !== 'setNodeKVPair') {
        this.processAction(action);
      }
    });

    // Persist optional feedback comment before unlock/batch so Turbo cannot drop an in-flight PATCH.
    if (this._isFeedbackSubmitButton()) {
      const commentSaved = await this.flushFeedbackCommentSave();
      if (!commentSaved) {
        return;
      }
    } else {
      // Free-text / notes submit: flush debounced value before graded+inactive patch.
      const notesSaved = await this.flushAdjacentNotesSave();
      if (!notesSaved) {
        return;
      }
    }

    // Feedback modal end-of-case submit should behave like Continue (unlock+turbo) so the pane swap
    // and finish UX are driven by the unlock flow, not an apply_state special-case.
    if (this.intentValue === 'unlock_feedback_submit' && isStudentUnlockContinueFlow()) {
      const currentSection = this.element.closest('.doc-section');
      const currentSectionUid = currentSection?.dataset?.uid;
      if (currentSectionUid) {
        const unlockPayload = await runStudentFeedbackSubmitUnlock({
          element: this.element,
          documentId: this.documentIdValue,
          userId: this.userIdValue,
          application: this.application,
          currentSectionUid,
        });

        if (unlockPayload && finishCaseCardJustRevealed(unlockPayload)) {
          this.handleCompletionModals(unlockPayload);
        }

        this.disableButton();
        this.handleSubmitUi(this.element);
        return;
      }
    }

    // Batch API calls
    let nodeKvPairActions = normalizedActions.filter(
      (a) => a.action === 'setNodeKVPair',
    );
    nodeKvPairActions = this.appendFeedbackSubmitClickedAction(nodeKvPairActions);

    if (!nodeKvPairActions.length) {
      return;
    }

    const response = await batchSetNodeKVPair(
      nodeKvPairActions,
      this.documentIdValue,
      this.userIdValue,
      this.application,
    );

    this.updateUiPostApiCall(response, nodeKvPairActions);
  }

  async flushAdjacentNotesSave() {
    const notesEl = this.element.previousElementSibling;
    if (!notesEl) {
      return true;
    }

    const controllers = notesEl.getAttribute('data-controller') || '';
    if (
      !controllers.includes('case-experience--notes') ||
      notesEl.hasAttribute('data-feedback-card')
    ) {
      return true;
    }

    const notes =
      this.application.getControllerForElementAndIdentifier(
        notesEl,
        'case-experience--notes',
      ) || this.application.getControllerForElementAndIdentifier(notesEl, 'notes');

    if (!notes || typeof notes.flushPendingSave !== 'function') {
      return true;
    }

    return notes.flushPendingSave();
  }

  async flushFeedbackCommentSave() {
    const scope =
      this.element.closest('.feedback-card-content') ||
      this.element.closest('.doc-section.feedback_card');
    if (!scope) {
      return true;
    }

    const notesEl = scope.querySelector(
      '[data-controller~="case-experience--notes"][data-feedback-card]',
    );
    if (!notesEl) {
      return true;
    }

    const notes =
      this.application.getControllerForElementAndIdentifier(
        notesEl,
        'case-experience--notes',
      ) || this.application.getControllerForElementAndIdentifier(notesEl, 'notes');

    if (!notes || typeof notes.flushPendingSave !== 'function') {
      return true;
    }

    return notes.flushPendingSave();
  }

  appendFeedbackSubmitClickedAction(actions) {
    if (!this._isFeedbackSubmitButton()) return actions;
    const buttonUid = this.uidValue;
    if (!buttonUid) return actions;

    const alreadyPresent = actions.some(
      (a) =>
        a.action === 'setNodeKVPair' &&
        a.arguments?.uid === buttonUid &&
        a.arguments?.key === 'clicked',
    );
    if (alreadyPresent) return actions;

    return [
      ...actions,
      {
        action: 'setNodeKVPair',
        arguments: { uid: buttonUid, key: 'clicked', value: true },
      },
    ];
  }

  _isFeedbackSubmitButton() {
    if (this.intentValue === 'unlock_feedback_submit') return true;
    if (this.uidValue === 'feedback_card_1_button') return true;
    return this.nameValue?.toLowerCase() === 'submit_&_finish_case';
  }

  updateUiPostApiCall(response, actions) {
    if (finishCaseCardJustRevealed(response)) {
      this.handleCompletionModals(response);
    }

    this.disableButton();
    this.handleSubmitUi(this.element);
    this.refreshCategoryMatcherAfterSubmit(response);
    if (this._isSubmitLike()) {
      const buttonUid = this.uidValue;
      const application = this.application;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const buttonControllerWrapper = document.querySelector(
            `[data-controller~="case-experience--button-action"][data-case-experience--button-action-uid-value="${CSS.escape(buttonUid)}"]`,
          );
          const buttonController = buttonControllerWrapper && application.getControllerForElementAndIdentifier(buttonControllerWrapper, 'case-experience--button-action');
          buttonController?.disableButton();
          if (buttonControllerWrapper && buttonController) {
            buttonControllerWrapper.setAttribute('data-case-experience--button-action-inactive-value', '2');
            buttonController.inactiveValue = 2;
          }
          const sectionElement =
            buttonControllerWrapper?.closest('.summary-statement-l1-card') ||
            buttonControllerWrapper?.closest('.doc-section');
          const aiSummaryFeedbackRoot =
            sectionElement?.querySelector('[data-controller~="case-experience--ai-summary-feedback"]') ||
            sectionElement?.querySelector('.doc-case-experience--summary-statement[data-controller~="case-experience--ai-summary-feedback"]');
          const aiSummaryFeedbackController =
            aiSummaryFeedbackRoot && application.getControllerForElementAndIdentifier(aiSummaryFeedbackRoot, 'case-experience--ai-summary-feedback');
          if (aiSummaryFeedbackController?.shouldLockAfterNonAiSubmit?.()) {
            if (aiSummaryFeedbackController.hasAiSummaryFeedbackBlockTarget) {
              aiSummaryFeedbackController.disableRating();
            }
            aiSummaryFeedbackController.lockSummaryStatementAfterNonAiSubmit({ afterSubmit: true });
            if (aiSummaryFeedbackController.hasDisclaimerTarget) {
              aiSummaryFeedbackController.disclaimerTarget.style.display = 'none';
            }
          }
          if (buttonControllerWrapper && buttonController) {
            buttonController.handleSubmitUi(buttonControllerWrapper);
          }
        });
      });
    }
    if (this.nameValue === 'continue') {
      this.pauseVideosInSectionContainingButton();
    }

    if (this._isSubmitLike()) {
      const domState = getDusFromDom();
      const apiState = response?.state && typeof response.state === 'object' ? response.state : {};
      const mergedState = mergeDocumentUserStateForContinueSync(domState, apiState);
      syncAllContinueButtonsForDocument(mergedState);
    }

    if (this.nameValue === 'continue') {
      scheduleScrollOpenedCardTitleIntoView();
    }
  }

  pauseVideosInSectionContainingButton() {
    const section = this.element.closest('.doc-section');
    if (!section) return;
    section.querySelectorAll('video').forEach((video) => {
      try {
        video.pause();
      } catch {
        // Ignore (e.g. media not loaded)
      }
    });
  }

  handleCompletionModals(apiPayload = null) {
    scheduleStudentCaseCompletionModalsAfterUnlock(apiPayload);
  }

  disableButton() {
    const innerButton = this.element.querySelector('button');
    if (innerButton) innerButton.disabled = true;
  }

  refreshCategoryMatcherAfterSubmit(response) {
    if (!this._isSubmitLike() || !response) return;

    const section = this.element.closest('.doc-section');
    if (!section) return;

    const categoryMatcherRoot = section.querySelector(
      '[data-controller~="case-experience--category-matcher"]',
    );
    if (!categoryMatcherRoot) return;

    const categoryMatcherController = this.application.getControllerForElementAndIdentifier(
      categoryMatcherRoot,
      'case-experience--category-matcher',
    );
    if (!categoryMatcherController?.refreshAfterSubmit) return;

    const matcherUid = normalizeNodeUid(categoryMatcherController.nodeUidValue);
    if (response.state?.[matcherUid]) {
      categoryMatcherController.nodeStateValue = response.state[matcherUid];
      categoryMatcherController._valueFromState = undefined;
    }
    categoryMatcherController.refreshAfterSubmit(response);
  }

  handleSubmitUi(submitButton) {
    if (!submitButton || !this._isSubmitLike()) return;

    let section = submitButton.closest('.doc-section');
    if (!section) return;

    const summaryStatementCard = submitButton.closest('.summary-statement-l1-card');
    const summaryStatementRedesign = Boolean(
      summaryStatementCard?.querySelector('.doc-case-experience--summary-statement'),
    );

    let questionNode = null;
    let answerCommentNode = null;
    let walkDepth = 0;
    const maxWalk = 24;
    while (section && walkDepth < maxWalk) {
      if (!questionNode) {
        questionNode = section.querySelector(
          '.doc-children [data-is-node] .doc-multiple-choice-wrapper',
        );
      }
      if (!answerCommentNode) {
        answerCommentNode =
          section.querySelector('.doc-children .answer_comment') ||
          section.querySelector('.answer_comment');
      }
      if (answerCommentNode) break;
      section = section.parentElement?.closest('.doc-section') || null;
      walkDepth += 1;
    }

    if (!answerCommentNode) {
      answerCommentNode = findAnswerCommentInActivePane(submitButton);
    }

    if (questionNode) {
      this.showGradedInfo(questionNode);
    }

    if (answerCommentNode && !this.aiSummaryStatementAnswerCommentShownValue && !summaryStatementRedesign) {
      revealAnswerCommentElement(answerCommentNode);
    }
  }

  showGradedInfo(questionNode) {
    const gradedInfos = questionNode.querySelectorAll(
      '.doc-multiple-choice-answer-graded-info',
    );

    gradedInfos.forEach((info) => {
      const parentLi = info.closest('li');
      if (!parentLi) return;

      if (parentLi.dataset.answerCorrect === 'true') {
        info.classList.remove('hidden');
        info.style.display = '';
      } else if (parentLi.dataset.answerCorrect === 'false') {
        info.classList.remove('hidden');
        info.classList.add('invisible');
      }
    });

    const gradedFeedback =
      questionNode.querySelector('.graded-feedback');
    gradedFeedback?.classList.remove('hidden');
  }

  processAction(action) {
    if (!action?.action) return;
    const { action: type, arguments: args } = action;
    switch (type) {
      case 'scrollTo':
        this.scrollTo(args);
        break;
      case 'openExternalLink':
        this.openExternalLink(args);
        break;
      case 'openInternalLink':
        this.openInternalLink(args);
        break;
      case 'setNodeKVPair':
        this.setNodeKVPair(args);
        break;
      default:
        console.log(`Unknown action: ${type}`);
    }
  }

  setNodeKVPair(args) {
    batchSetNodeKVPair(
      [{ action: 'setNodeKVPair', arguments: args }],
      this.documentIdValue,
      this.userIdValue,
      this.application,
    );
  }

  scrollTo(args) {
    const nodeUid = args?.uid;
    if (!nodeUid) return;
    setTimeout(() => {
      const element = document.querySelector(`[data-uid="${nodeUid}"]`);
      if (element) {
        const yOffset = -125; // Offset for fixed header
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  }

  openExternalLink(args) {
    if (args?.url) {
      window.open(args.url, '_blank');
    }
  }

  openInternalLink(args) {
    if (args?.path) {
      window.open(args.path, '_blank');
    }
  }

}
